import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

// react-routers components
import { Link } from "react-router-dom";

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import MDActionConfigurator from "components/MDActionConfigurator";

import {
  updateUser,
  userPasswordReset,
  activateDeactivateUser,
} from "actions/userActions";
import MDDialog from "components/MDDialog";

function ProfileSettingsCard({
  title,
  active,
  info,
  currentInfo,
  id,
  social,
  action,
  shadow,
}) {
  const configuratorRef = useRef(null);
  const dispatch = useDispatch();
  const [currentState, setCurrentState] = useState("");
  const [currentData, setCurrentData] = useState({
    _id: "",
    name: "",
    email: "",
    phone: "",
    idNumber: "",
    role: "user",
    image: "",
  });

  const handleToggleBackdrop = (item, state) => {
    setCurrentData(item);
    setCurrentState(state);
    if (configuratorRef.current) {
      configuratorRef.current.handleToggleConfigurator();
    }
  };

  const roleOptions = [
    {
      name: "Super Admin",
      value: "super_admin",
    },
    {
      name: "Admin",
      value: "admin",
    },
    {
      name: "Agent",
      value: "agent",
    },
    {
      name: "User",
      value: "user",
    },
  ];

  const handleSubmitUser = (e) => {
    dispatch(updateUser(id, e));
    handleToggleBackdrop({}, "");
  };
  const handleAgreeActivateDeactivate = () => {
    dispatch(activateDeactivateUser(id, { active: !currentInfo?.active }));
  };

  const handleAgreePasswordReset = () => {
    console.log("🚀 ~ file: index.js:88 ~ handleAgree ~ handleAgree:");
    const myData = {
      data: {
        type: "password-forgot",
        attributes: {
          redirect_url: `${process.env.REACT_APP_URL}/auth/reset-password`,
          email: currentInfo?.email,
        },
      },
    };
    dispatch(userPasswordReset(myData));
  };

  return (
    <>
      <MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={2}
          px={2}
        >
          <MDTypography
            variant="h6"
            fontWeight="medium"
            textTransform="capitalize"
            sx={{
              color: ({ palette: { text } }) => text.darkAccent,
            }}
          >
            {title}
          </MDTypography>
        </MDBox>

        <Divider />

        <MDBox p={2}>
          <MDBox mb={4} lineHeight={1}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <MDBox width="80%">
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{
                    color: ({ palette: { text } }) => text.darkAccent,
                  }}
                >
                  Edit Account Information
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography
                  component={Link}
                  to={action.route}
                  variant="body2"
                  sx={{
                    color: ({ palette: { text } }) => text.darkAccent,
                  }}
                  onClick={() => handleToggleBackdrop(currentInfo, "edit")}
                >
                  <Tooltip title={action.tooltip} placement="top">
                    <Icon
                      fontSize="medium"
                      sx={{ marginLeft: 2, marginRight: 2 }}
                    >
                      edit
                    </Icon>
                  </Tooltip>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          <MDBox mb={4} lineHeight={1}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <MDBox width="80%">
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{
                    color: ({ palette: { text } }) => text.darkAccent,
                  }}
                >
                  Account is {active ? " Active" : " Inactive"}
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDDialog
                  title={`${active ? "Deactivate " : "Activate "} ${
                    currentInfo?.name
                  }'s Account`}
                  details={`Are You sure you want to ${
                    active ? " Deactivate " : " Activate "
                  } ${currentInfo?.name}'s account?`}
                  onAgree={handleAgreeActivateDeactivate}
                  customButton={<Switch checked={active} />}
                />
              </MDBox>
            </MDBox>
          </MDBox>

          <MDBox mb={4} lineHeight={1}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <MDBox width="80%">
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{
                    color: ({ palette: { text } }) => text.darkAccent,
                  }}
                >
                  {`Reset ${currentInfo?.name}'s Account Password`}
                </MDTypography>
              </MDBox>
              <MDBox>
                <Tooltip
                  title="Reset this account's password and send the account owner an email with instructions"
                  placement="top"
                >
                  <MDDialog
                    title={`Reset ${currentInfo?.name}'s Account Password`}
                    details={`Are You sure you want to reset ${currentInfo?.name}'s account password?`}
                    onAgree={handleAgreePasswordReset}
                    customButton={
                      <Icon
                        fontSize="medium"
                        sx={{ marginLeft: 2, marginRight: 2 }}
                      >
                        lock_reset
                      </Icon>
                    }
                  />
                </Tooltip>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>

      <MDActionConfigurator
        ref={configuratorRef}
        title="Admin User"
        onCompleteConfig={handleSubmitUser}
        data={[
          {
            keyType: "_id",
            valueType: currentData?._id,
          },
          {
            labelType: "Name",
            placeholderType: "Enter Name",
            dataType: "text",
            keyType: "name",
            valueType: currentData?.name,
          },
          {
            labelType: "Email",
            placeholderType: "Enter Email",
            dataType: "email",
            keyType: "email",
            valueType: currentData?.email,
          },
          {
            labelType: "Phone",
            placeholderType: "Enter Phone",
            dataType: "phone",
            keyType: "phone",
            valueType: currentData?.phone,
          },
          {
            labelType: "National ID Number",
            placeholderType: "Enter National ID Number",
            dataType: "text",
            keyType: "idNumber",
            valueType: currentData?.idNumber,
          },
          {
            labelType: "Role",
            placeholderType: "Choose Role",
            dataType: "select",
            keyType: "role",
            valueType: currentData?.role,
            options: roleOptions,
          },
          {
            labelType: "Profile Image",
            placeholderType: "Enter Profile Image",
            dataType: "image",
            keyType: "image",
            valueType: currentData?.profile_image,
          },
        ]}
        state={currentState}
      />
    </>
  );
}

// Setting default props for the ProfileSettingsCard
ProfileSettingsCard.defaultProps = {
  shadow: true,
};

// Typechecking props for the ProfileSettingsCard
ProfileSettingsCard.propTypes = {
  title: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  info: PropTypes.objectOf(PropTypes.string).isRequired,
  // social: PropTypes.arrayOf(PropTypes.object).isRequired,
  action: PropTypes.shape({
    route: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
  }).isRequired,
  shadow: PropTypes.bool,
};

export default ProfileSettingsCard;
